import React, { useState, useEffect, ChangeEvent, FormEvent, useRef } from 'react';
import { getTeams, createTeam, updateTeam, deleteTeam, Team, TeamFormData } from '../../api/teamsApi';
import { getGroups, Group } from '../../api/groupsApi';
import Layout from '../../components/Layout';
import { PlusIcon, PencilIcon, TrashIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import TableHeader from '../../components/TableHeader';
import SearchFilter from '../../components/SearchFilter';
import { sortData, filterData, SortState } from '../../utils/tableUtils';
import Table, { TableColumn } from '../../components/Table';

// Add a new interface for CSV import results
interface ImportResult {
  total: number;
  success: number;
  failures: number;
  errors: string[];
}

const Teams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [displayedTeams, setDisplayedTeams] = useState<Team[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<TeamFormData>({
    id: '',
    name: '',
    logo_url: '',
    group_id: '',
    rank: null
  });
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState<Team>>({
    key: 'id',
    direction: 'asc'
  });
  
  // Add state for CSV import
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch teams and groups on component mount
  useEffect(() => {
    fetchTeamsAndGroups();
  }, []);

  // Apply sorting and filtering whenever the base data, search term, or sort state changes
  useEffect(() => {
    let filteredData = teams;
    
    // Apply search filtering
    if (searchTerm) {
      filteredData = filterData(
        teams, 
        searchTerm, 
        ['id', 'name', 'group_id', 'logo_url']
      );
    }
    
    // Apply sorting
    filteredData = sortData(filteredData, sortState.key, sortState.direction);
    
    setDisplayedTeams(filteredData);
  }, [teams, searchTerm, sortState]);

  const fetchTeamsAndGroups = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Wrap API calls in Promise.all and handle potential null responses
      const [teamsResponse, groupsResponse] = await Promise.all([
        getTeams().catch(() => []),
        getGroups().catch(() => [])
      ]);
      
      setTeams(teamsResponse);
      setGroups(groupsResponse);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
      // Set empty arrays to prevent null reference errors
      setTeams([]);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (team: Team | null = null): void => {
    if (team) {
      // Edit mode
      setEditingTeam(team);
      setFormData({
        id: team.id,
        name: team.name,
        logo_url: team.logo_url || '',
        group_id: team.group_id || '',
        rank: team.rank ?? null
      });
    } else {
      // Create mode
      setEditingTeam(null);
      setFormData({
        id: '',
        name: '',
        logo_url: '',
        group_id: '',
        rank: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setError('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    if (name === 'rank') {
      setFormData({ ...formData, rank: value === '' ? null : parseInt(value, 10) });
    } else if ((name === 'id' || name === 'group_id') && value !== '' && /^\d+$/.test(value)) {
      setFormData({ ...formData, [name]: parseInt(value, 10) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = (): boolean => {
    if (!formData.id) {
      setError('Team ID is required');
      return false;
    }
    if (!formData.name) {
      setError('Team name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      if (editingTeam) {
        // Update existing team
        await updateTeam(Number(formData.id), formData);
      } else {
        // Create new team
        await createTeam(formData);
      }
      
      // Refresh teams list
      fetchTeamsAndGroups();
      closeModal();
    } catch (err) {
      console.error('Error saving team:', err);
      setError('Failed to save team. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      setIsLoading(true);
      try {
        await deleteTeam(id);
        fetchTeamsAndGroups();
      } catch (err) {
        console.error('Error deleting team:', err);
        setError('Failed to delete team. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Function to find group name by id
  const getGroupName = (groupId: number | null): string => {
    if (!groupId) return 'None';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown';
  };

  // Function to handle CSV file import
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setIsImporting(true);
        setError('');
        setImportResult(null);
        
        const csv = event.target?.result as string;
        const result = await importTeamsFromCSV(csv);
        setImportResult(result);
        
        // Refresh teams list after import
        await fetchTeamsAndGroups();
      } catch (err) {
        console.error('Error importing teams:', err);
        setError('Failed to import teams. Please check the CSV format and try again.');
      } finally {
        setIsImporting(false);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    
    reader.readAsText(file);
  };

  // Function to parse CSV and create teams
  const importTeamsFromCSV = async (csv: string): Promise<ImportResult> => {
    const lines = csv.split('\n');
    const result: ImportResult = {
      total: 0,
      success: 0,
      failures: 0,
      errors: []
    };
    
    // Skip header row if it exists (assumes first row is header)
    const startRow = lines[0].toLowerCase().includes('id') || lines[0].toLowerCase().includes('name') ? 1 : 0;
    
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines
      
      result.total++;
      
      const values = line.split(',');
      if (values.length < 2) {
        result.failures++;
        result.errors.push(`Row ${i+1}: Not enough columns`);
        continue;
      }
      
      try {
        const teamId = values[0].trim();
        const teamName = values[1].trim();
        const groupId = values.length > 2 ? values[2].trim() : '';
        const logoUrl = values.length > 3 ? values[3].trim() : '';
        
        if (!teamId || !teamName) {
          result.failures++;
          result.errors.push(`Row ${i+1}: Missing ID or name`);
          continue;
        }
        
        // Create the team
        await createTeam({
          id: parseInt(teamId, 10),
          name: teamName,
          group_id: groupId ? parseInt(groupId, 10) : null,
          logo_url: logoUrl
        });
        
        result.success++;
      } catch (err: any) {
        result.failures++;
        const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
        result.errors.push(`Row ${i+1}: ${errorMessage}`);
      }
    }
    
    return result;
  };

  // Function to trigger file input click
  const triggerFileInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle sort column click
  const handleSort = (key: keyof Team) => {
    setSortState(prevState => ({
      key,
      direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Define table columns
  const columns: TableColumn<Team>[] = [
    {
      key: 'id',
      title: 'ID',
      render: (team) => <span className="text-gray-400">{team.id}</span>,
      sortable: true
    },
    {
      key: 'name',
      title: 'Name',
      render: (team) => <span className="text-white">{team.name}</span>,
      sortable: true
    },
    {
      key: 'group_id',
      title: 'Group',
      render: (team) => <span className="text-gray-300">{getGroupName(team.group_id)}</span>,
      sortable: true
    },
    {
      key: 'logo_url',
      title: 'Logo URL',
      render: (team) => team.logo_url ? (
        <a href={team.logo_url} target="_blank" rel="noopener noreferrer" className="text-stake-blue hover:text-blue-300">
          View Logo
        </a>
      ) : <span className="text-gray-400">None</span>,
      sortable: true
    },
    {
      key: 'rank',
      title: 'Rank (Worst Pool)',
      render: (team) => team.rank != null
        ? <span className="text-yellow-400 font-medium">{team.rank}</span>
        : <span className="text-gray-500">—</span>,
      sortable: true
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (team) => (
        <div className="flex space-x-2">
          <button 
            className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
            onClick={() => openModal(team)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button 
            className="text-stake-red hover:text-red-400 p-1 rounded hover:bg-dark-200 transition-colors"
            onClick={() => handleDelete(team.id)}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="-mx-3 sm:mx-0 sm:px-4 py-4">
        <div className="flex justify-between items-center mb-6 px-3 sm:px-0">
          <div className="flex items-center space-x-4">
            <SearchFilter
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="חפש קבוצה..."
              className="w-64"
            />
            <button 
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              onClick={triggerFileInput}
            >
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              Import CSV
            </button>
            <button 
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => openModal()}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Team
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}

        {importResult && (
          <div className={`${importResult.failures === 0 ? 'bg-stake-green bg-opacity-20 border-stake-green text-white' : 'bg-yellow-100 border-yellow-400 text-yellow-700'} px-4 py-3 rounded mb-4 border`}>
            <p>Import results: {importResult.success} of {importResult.total} teams imported successfully.</p>
            {importResult.failures > 0 && (
              <div>
                <p className="font-bold mt-2">Errors ({importResult.failures}):</p>
                <ul className="list-disc pl-5">
                  {importResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          className="hidden"
        />

        <Table<Team>
          fullWidth
          compact
          data={displayedTeams}
          columns={columns}
          keyExtractor={(team) => team.id}
          emptyMessage="No teams found"
          isLoading={isLoading && !isModalOpen}
          sortState={sortState}
          onSort={handleSort}
          searchTerm={searchTerm}
        />

        {/* Modal for add/edit team */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
            <div className="bg-dark-100 rounded-lg overflow-hidden shadow-xl transform w-full max-w-lg">
              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                  <div className="text-lg font-medium text-white mb-4">
                    {editingTeam ? 'Edit Team' : 'Add New Team'}
                  </div>

                  {error && (
                    <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}

                  <div className="mb-4">
                    <label htmlFor="id" className="block text-sm font-medium text-gray-300">
                      Team ID
                    </label>
                    <input
                      type="text"
                      id="id"
                      name="id"
                      value={formData.id}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                      required
                      readOnly={!!editingTeam}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                      Team Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="logo_url" className="block text-sm font-medium text-gray-300">
                      Logo URL
                    </label>
                    <input
                      type="text"
                      id="logo_url"
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                    />
                    {formData.logo_url && (
                      <div className="mt-2">
                        <img
                          src={formData.logo_url as string}
                          alt="Team logo preview"
                          className="h-16 w-16 object-contain border border-stake-border rounded"
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = 'https://via.placeholder.com/64?text=Error';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="group_id" className="block text-sm font-medium text-gray-300">
                      Group
                    </label>
                    <select
                      id="group_id"
                      name="group_id"
                      value={formData.group_id as string}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                    >
                      <option value="">-- Select Group --</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="rank" className="block text-sm font-medium text-gray-300">
                      Rank (Worst Pool)
                    </label>
                    <p className="text-xs text-gray-500 mb-1">Set a number to include this team in the "best of worst" bet pool. Leave empty to exclude.</p>
                    <input
                      type="number"
                      id="rank"
                      name="rank"
                      value={formData.rank ?? ''}
                      onChange={handleInputChange}
                      min={1}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                      placeholder="e.g. 1, 2, 3..."
                    />
                  </div>
                </div>

                <div className="bg-dark-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-stake-blue text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-stake-border shadow-sm px-4 py-2 bg-dark-300 text-base font-medium text-gray-300 hover:bg-dark-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Teams; 